import { useState, useMemo } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { cn } from '@/lib/utils'
import { useAccount } from '@/contexts/AccountContext'
import { useConsumableBalances } from '@/features/consumable-balances/queries/consumableBalances'
import { useRedisBalances, useAccountDailyConsumables, useLiveCampaignEventCounts } from '../queries/balanceInspector'
import type { ConsumableBalance } from '@/features/consumable-balances/types/consumableBalance.types'
import type { RedisBalanceEntry, LiveCampaignEventCounts, ChannelBreakdown } from '../types/balanceInspector.types'
import type { EventConsumptionRow } from '@/api/types'

type MessageType = 'promotional' | 'transactional'

const CHANNELS = ['EMAIL', 'SMS', 'WHATSAPP', 'RCS', 'WEB_PUSH', 'MOBILE_PUSH'] as const
const MESSAGE_TYPES: MessageType[] = ['promotional', 'transactional']

const DAILY_EVENT_FIELD: Record<(typeof CHANNELS)[number], keyof EventConsumptionRow> = {
  EMAIL: 'emailOut',
  SMS: 'smsOut',
  WHATSAPP: 'whatsappOut',
  RCS: 'rcsOut',
  WEB_PUSH: 'webPushOut',
  MOBILE_PUSH: 'mobilePushOut',
}

/** Channels campaign_events can split by message type (email/sms/whatsapp/rcs) — web push and
 *  mobile push have no split available there, so they're handled as plain totals instead. */
const LIVE_BREAKDOWN_FIELD: Partial<Record<(typeof CHANNELS)[number], keyof LiveCampaignEventCounts>> = {
  EMAIL: 'email',
  SMS: 'sms',
  WHATSAPP: 'whatsapp',
  RCS: 'rcs',
}

const LIVE_TOTAL_FIELD: Partial<Record<(typeof CHANNELS)[number], keyof LiveCampaignEventCounts>> = {
  WEB_PUSH: 'webPushSent',
  MOBILE_PUSH: 'mobilePushSent',
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function formatValue(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : v.toLocaleString()
}

function formatTtl(seconds: number | null | undefined): string {
  // Backend omits null fields entirely (spring.jackson.default-property-inclusion=non_null), so
  // an absent TTL comes through as undefined, not null — check both.
  if (seconds == null || seconds < 0) return '—'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

export function BalanceInspector() {
  const { selectedAccountId } = useAccount()
  const accountId = selectedAccountId && selectedAccountId !== 'ALL' ? selectedAccountId : null
  const [date, setDate] = useState(todayIso())

  const mongo = useConsumableBalances(accountId)
  const redis = useRedisBalances(accountId)
  const daily = useAccountDailyConsumables(accountId, date)
  const live = useLiveCampaignEventCounts(accountId, date)

  const mongoByChannel = useMemo(() => {
    const map = new Map<string, ConsumableBalance>()
    for (const b of mongo.data ?? []) map.set(b.channel, b)
    return map
  }, [mongo.data])

  const redisByKey = useMemo(() => {
    const map = new Map<string, RedisBalanceEntry>()
    for (const r of redis.data ?? []) map.set(`${r.channel}:${r.messageType}`, r)
    return map
  }, [redis.data])

  const refreshAll = () => {
    mongo.refetch()
    redis.refetch()
    daily.refetch()
    live.refetch()
  }

  if (!accountId) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 mx-auto mb-4">
          <AlertCircle className="h-7 w-7 text-amber-600" />
        </div>
        <p className="text-gray-600">Select an account (top bar) to inspect its balances.</p>
      </div>
    )
  }

  const anyLoading = mongo.isLoading || redis.isLoading || daily.isLoading || live.isLoading
  const isFetching = mongo.isFetching || redis.isFetching || daily.isFetching || live.isFetching

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Balance Inspector — Account {accountId}</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Compares the Redis balance cache, account_consumables (Mongo), the Batched
            (daily_entity_event_metrics MV) event count, and a Live query straight off
            campaign_events — to help debug why a balance looks stale or which layer is behind.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Event date</label>
            <input
              type="date"
              value={date}
              max={todayIso()}
              onChange={(e) => setDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <Button variant="outline" size="sm" onClick={refreshAll} disabled={isFetching}>
            <RefreshCw className={cn('h-4 w-4 mr-1.5', isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {anyLoading ? (
        <div className="p-8">
          <LoadingState message="Loading balance data…" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Channel</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Type</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Redis</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Redis TTL</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Mongo Available</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Mongo Reserved</th>
                <th
                  className="text-right px-4 py-3 font-medium text-gray-600 whitespace-nowrap"
                  title="daily_entity_event_metrics — a StarRocks materialized view, refreshed on its own schedule. No promotional/transactional split."
                >
                  Batched
                </th>
                <th
                  className="text-right px-4 py-3 font-medium text-gray-600 whitespace-nowrap"
                  title="Queried directly off campaign_events — uncached. Email/SMS/WhatsApp/RCS are split by message type; web push/mobile push are not."
                >
                  Live (Campaign Events)
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Status</th>
              </tr>
            </thead>
            <tbody>
              {redis.isError || mongo.isError || daily.isError || live.isError ? (
                <tr>
                  <td colSpan={9} className="p-8">
                    <ErrorState
                      message={
                        (redis.error as Error)?.message ||
                        (mongo.error as Error)?.message ||
                        (daily.error as Error)?.message ||
                        (live.error as Error)?.message ||
                        'Failed to load balance data'
                      }
                      onRetry={refreshAll}
                    />
                  </td>
                </tr>
              ) : (
                CHANNELS.map((channel) =>
                  MESSAGE_TYPES.map((messageType, typeIdx) => {
                    const mongoBal = mongoByChannel.get(channel)
                    const mongoAvailable =
                      messageType === 'promotional' ? mongoBal?.promoAvailable : mongoBal?.transactionalAvailable
                    const mongoReserved =
                      messageType === 'promotional' ? mongoBal?.reservedPromo : mongoBal?.reservedTransactional
                    const redisEntry = redisByKey.get(`${channel}:${messageType}`)
                    const eventCount = daily.data?.[DAILY_EVENT_FIELD[channel]] as number | undefined

                    const breakdownField = LIVE_BREAKDOWN_FIELD[channel]
                    const totalField = LIVE_TOTAL_FIELD[channel]
                    const liveBreakdown = breakdownField
                      ? (live.data?.[breakdownField] as ChannelBreakdown | undefined)
                      : undefined
                    const liveValue = liveBreakdown
                      ? messageType === 'promotional'
                        ? liveBreakdown.promotional
                        : liveBreakdown.transactional
                      : totalField
                        ? (live.data?.[totalField] as number | undefined)
                        : undefined

                    let status: { label: string; variant: 'success' | 'warning' | 'secondary' } = {
                      label: 'No Redis key',
                      variant: 'secondary',
                    }
                    if (redisEntry?.exists) {
                      const netMongo =
                        mongoAvailable !== undefined && mongoReserved !== undefined
                          ? mongoAvailable - mongoReserved
                          : undefined
                      status =
                        netMongo !== undefined && redisEntry.value !== null && netMongo === redisEntry.value
                          ? { label: 'Match', variant: 'success' }
                          : { label: 'Diverged', variant: 'warning' }
                    }

                    return (
                      <tr
                        key={`${channel}:${messageType}`}
                        className={cn(
                          'border-b border-gray-100',
                          typeIdx === MESSAGE_TYPES.length - 1 && 'border-b-2 border-b-gray-200'
                        )}
                      >
                        {typeIdx === 0 && (
                          <td
                            rowSpan={MESSAGE_TYPES.length}
                            className="px-4 py-3 font-medium text-gray-900 align-top whitespace-nowrap"
                          >
                            {channel}
                          </td>
                        )}
                        <td className="px-4 py-3 text-gray-600 capitalize whitespace-nowrap">{messageType}</td>
                        <td className="px-4 py-3 text-right font-mono whitespace-nowrap">
                          {formatValue(redisEntry?.value ?? null)}
                        </td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {formatTtl(redisEntry?.ttlSeconds ?? null)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono whitespace-nowrap">
                          {formatValue(mongoAvailable)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-gray-500 whitespace-nowrap">
                          {formatValue(mongoReserved)}
                        </td>
                        {typeIdx === 0 && (
                          <td
                            rowSpan={MESSAGE_TYPES.length}
                            className="px-4 py-3 text-right font-mono text-gray-700 align-top whitespace-nowrap"
                          >
                            {formatValue(eventCount)}
                          </td>
                        )}
                        {breakdownField ? (
                          <td className="px-4 py-3 text-right font-mono text-gray-700 whitespace-nowrap">
                            {formatValue(liveValue)}
                          </td>
                        ) : (
                          typeIdx === 0 && (
                            <td
                              rowSpan={MESSAGE_TYPES.length}
                              className="px-4 py-3 text-right font-mono text-gray-700 align-top whitespace-nowrap"
                            >
                              {formatValue(liveValue)}
                            </td>
                          )
                        )}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Badge
                            variant={status.variant}
                            title={
                              status.label === 'No Redis key'
                                ? 'Expected if this account/channel/type has had no Journey, Single-User, or transactional send on an FH-managed vendor in the last 48h — batch campaigns never touch Redis.'
                                : undefined
                            }
                          >
                            {status.label}
                          </Badge>
                        </td>
                      </tr>
                    )
                  })
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
